import { useDispatch, useSelector } from 'react-redux';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import TwoLevelTabNavigation from 'components/Navigation/TwoLevelTabNavigation';
import { selectShowSecondLevelTabs } from 'selectors/partnerFormSelectors';
import theme from 'theme';
import palette from 'theme/palette';
import { GlobalState } from 'reducers/stateTypes';
import { useFetchLegalPartner } from 'hooks/queries/useFetchPartner';
import { usePartnerDlmV2Enabled } from 'hooks/queries/usePartnerDlmV2Enabled';
import { User } from 'types/User';
import { PartnerType } from 'types/Partner';
import { ONE_LEVEL_NAV_FRAME_HEIGHT, TWO_LEVEL_NAV_FRAME_HEIGHT } from 'services/constants';
import { setTabId } from 'reducers/partnerFormReducer';
import { TabContext } from '@mui/lab';
import { AppBar, Box, Button, Grid, SvgIcon, Tooltip, Typography, Skeleton } from '@mui/material';
import { ReactComponent as GlobalLE } from 'assets/icons/LE_Global.svg';
import { ReactComponent as LegalEntityIcon } from 'assets/icons/LE.svg';
import RejectWithdrawButton from 'views/PartnerTabs/RejectWithdrawButton';
import { formatEnum, getPartnerPageTitle } from 'utils/stringUtil';
import PartnerFrontendIFrame from 'views/Frames/PartnerFrontendIFrame';
import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { getIFrame } from 'utils/browserUtil';
import { useHost } from 'contexts/Host';
import { openModal } from 'reducers/modalReducer';
import { useFetchBusinessRelations } from 'hooks/queries/useFetchClmBrs';
import { getPartnerProfile } from 'services/partnerOrchestratorService';
import { PartnerFormCategories, PartnerFormTabItems } from 'views/PartnerTabs/PrivateIndividualFormTabs';
import { BrContext, BrProfileType, BrSearchDto, BrType, BrTypes } from 'types/BrTypes';
import PartnerTabsMessageHandler from 'views/PartnerTabs/PartnerTabsMessageHandler';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { assertNotNullOrUndefined, isNotNullOrUndefined, isNullOrUndefined, isStringNotBlank } from 'utils/utils';
import { selectDlmStatus, selectDlmVersion } from 'selectors/dlmSelector';
import { JBColors, JBIcon } from '@bjb-gcrm/gcrm-frontend-components';
import { useFetchPartnerLockStatus } from 'hooks/queries/useFetchPartnerLockStatus';
import { Lock } from 'views/Lock/Lock';
import { DlmStatus } from 'views/DraftMode/DraftSwitch';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalIncomingPostMessageTypes, ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { useDetectMultipleTabs } from 'hooks/useDetectMultipleTabs';
import { MultiTabWarning } from 'components/Common/MultiTabWarning';
import { useFetchPartnerVersions } from 'hooks/queries/useFetchPartnerVersions';
import { resetDlmState } from 'reducers/dlmReducer';
import PageTitleToken from 'components/Common/PageTitleToken';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { SanctionsPartnerType } from 'views/PartnerTabs/SanctionsPartnerType';

export const partnerTabStyles = {
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  title: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  partnerId: {
    fontSize: '12px',
  },
  titleIcon: {
    height: '28px',
    width: '28px',
  },
  superscript: {
    fontSize: '8px',
    color: palette.primary.main,
  },
  topBar: {
    position: 'sticky',
    zIndex: 1,
    top: 0,
    left: 0,
    margin: 0,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    backgroundColor: '#FFF',
  },
  hidden: {
    display: 'none',
  },
  tabIconWrapper: {
    flexDirection: 'row-reverse',
  },
  tabIconLabelContainer: {
    width: 'auto',
    padding: 0,
    minHeight: 'unset',
    marginLeft: '25px',
  },
  disabledTab: {
    marginLeft: '25px',
    '&.MuiButtonBase-root.Mui-disabled': {
      pointerEvents: 'auto',
    },
  },
};

/**
 * The switchable tabs on top of the Legal Partner form.
 */
const LegalPartnerFormTabs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { shadowRoot } = useHost();
  const legalPartnerFormTab: PartnerFormTabItems = useSelector((state: GlobalState) => state.partnerForm.tabIndex);
  const dispatch = useDispatch();
  const { id: techId } = useParams<{ id: string }>();
  assertNotNullOrUndefined(techId);
  const hasMultipleTabs = useDetectMultipleTabs(techId);
  const { data: partnerVersions = [], isFetching: isVersionsFetching } = useFetchPartnerVersions(techId);

  const runtimeConfig = useRuntimeConfig();
  const { data: partner, isFetching, refetch } = useFetchLegalPartner(techId);
  const user: User = useSelector((state: GlobalState) => state.user?.userDetails);
  const userId: string = user?.username;
  const isUserApac: boolean = user?.isApac;
  const caseId = useSelector((state: GlobalState) => state.caseInformation.caseId);
  const pcoUrl: string = caseId ? `/partner/${techId}/case/${caseId}/update` : `/partner?partnerNumber=${techId}`;
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);
  const urlHash = location.hash;
  const distributeDocument = queryParams.get('distributeDocument');
  const clientClassificationId = queryParams.get('clientClassificationId');
  const urlSearchDocumentId = queryParams.get('documentId');
  const wizardBrUid = queryParams.get('wizardBrUid');
  const wizardRole = queryParams.get('wizardRole');
  const basePcdV2Url = `/partner/${techId}`;
  const [pcdV2DistributionUrl, setPcdV2DistributionUrl] = useState(`${basePcdV2Url}/distributions-overview`);
  const [pcdV2DocOverviewUrl, setPcdV2DocOverviewUrl] = useState(`${basePcdV2Url}/documents-overview`);
  const [intendedRole, setIntendedRole] = useState('');
  const [brProfile, setBrProfile] = useState<BrProfileType | undefined>(undefined);
  const [brContext, setBRContext] = useState<BrContext | null>(null);
  const brType = brContext?.brType ?? '';
  const newNote = queryParams?.get('newNote');
  const { tab = PartnerFormTabItems.CORE_DATA } = useParams<{ tab: PartnerFormTabItems }>();
  const { data: brItems, refetch: fetchBrRelations } = useFetchBusinessRelations(techId, false, true);
  const { data: partnerLocks = [] } = useFetchPartnerLockStatus(techId);
  const partnerHasIntermediaryBrs = brItems?.some((br: BrSearchDto) => br.busRelType === BrTypes.INTERMEDIARY_B2B);
  const partnerName = partner?.name ?? 'New legal entity';
  const [selectedBrContext, setSelectedBrContext] = useState(brContext);
  const [selectedIntendedRole, setSelectedIntendedRole] = useState<string>('');
  const [selectedProfileCode, setSelectedProfileCode] = useState<string>('');
  const tabRef = useRef(tab);
  const tabsNeedingSaveValidation = [
    PartnerFormTabItems.CORE_DATA,
    PartnerFormTabItems.RELATIONS,
    PartnerFormTabItems.CONTACT_NOTES,
    PartnerFormTabItems.RISK_CONSIDERATIONS,
    PartnerFormTabItems.INVESTMENT_PROFILE,
    PartnerFormTabItems.PARTNER_PROFILE,
    PartnerFormTabItems.WEALTH_OVERVIEW,
  ];
  const { postMessageToIFrameByType, postSetPartnerProfileMessage } = usePostMessageToIFrame();
  const isPartnerLockEnabled = runtimeConfig.configData.featureFlags?.partnerLock?.enabled;
  const isPartnerDlmV2 = usePartnerDlmV2Enabled();
  const isContactNoteTabEnabled =
    runtimeConfig.configData.featureFlags?.contactNote?.enabled || !partner?.imported || partnerHasIntermediaryBrs;
  const dlmStatus = useSelector(selectDlmStatus);
  const dlmVersion = useSelector(selectDlmVersion);
  const [initialDlmStatusAndVersion, setInitialDlmStatusAndVersion] = useState<{
    status: DlmStatus;
    version: number;
  } | null>(null);
  // DLM v2: the iframe resolves its own status/version, the portal passes none.
  const dlmUrlParams = isPartnerDlmV2
    ? ''
    : `&partnerStatus=${initialDlmStatusAndVersion?.status}&version=${initialDlmStatusAndVersion?.version}`;
  const showSecondLevelTabs = useSelector(selectShowSecondLevelTabs);
  const frameHeight = showSecondLevelTabs ? TWO_LEVEL_NAV_FRAME_HEIGHT : ONE_LEVEL_NAV_FRAME_HEIGHT;
  const isProfilingWizardBrChangesEnabled = runtimeConfig.configData.featureFlags?.profilingWizardBrChanges?.enabled;
  const partnerStructureSubType = isUserApac ? undefined : partner?.partnerStructureSubType;
  const isUatEnvironment = runtimeConfig.configData.env === 'pcm-uat1' || runtimeConfig.configData.env === 'pcm-uat2';

  const { data: partnerProfile } = useQuery({
    queryKey: [
      'getPartnerProfile',
      intendedRole,
      brType,
      brProfile,
      partnerStructureSubType,
      brContext?.brSubType,
      partner?.operationalType,
      partner?.partnerType,
    ],
    queryFn: () =>
      getPartnerProfile(
        brType as BrType,
        intendedRole,
        partner.partnerType,
        partnerStructureSubType,
        brContext?.brSubType,
        partner?.operationalType,
        brProfile,
      ),
    enabled: isNotNullOrUndefined(intendedRole) && isNotNullOrUndefined(brType),
  });

  useEffect(() => {
    if (
      isNullOrUndefined(initialDlmStatusAndVersion) &&
      isNotNullOrUndefined(dlmStatus) &&
      isNotNullOrUndefined(dlmVersion)
    ) {
      setInitialDlmStatusAndVersion({ status: dlmStatus, version: dlmVersion });
    }
  }, [dlmStatus, dlmVersion]);

  useEffect(() => {
    if (
      isPartnerDlmV2 !== false ||
      isNullOrUndefined(initialDlmStatusAndVersion) ||
      isNullOrUndefined(dlmStatus) ||
      isNullOrUndefined(dlmVersion) ||
      isNullOrUndefined(partnerVersions) ||
      isVersionsFetching
    ) {
      return;
    }

    // Validate that the exact status/version pair still exists
    const matchingVersion = partnerVersions.find((x) => x.partnerStatus === dlmStatus && x.version === dlmVersion);

    // During discard/recreate flow the old draft may already be discarded.
    // In that case do NOT trigger a stale RE_FETCH_PARTNER call.
    if (isNullOrUndefined(matchingVersion)) {
      return;
    }

    postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.RE_FETCH_PARTNER, {
      partnerStatus: dlmStatus,
      version: dlmVersion,
    });
  }, [dlmStatus, dlmVersion, partnerVersions, isVersionsFetching, initialDlmStatusAndVersion]);

  useEffect(() => {
    if (isNotNullOrUndefined(dlmStatus) && isNotNullOrUndefined(dlmVersion)) {
      setInitialDlmStatusAndVersion({ status: dlmStatus, version: dlmVersion });
    }
  }, [tab]);

  const isPartnerProfileLoading = !!intendedRole && !partnerProfile;
  const profilingWizardParams =
    !!partner && !intendedRole && brType
      ? ''
      : `?intendedRole=${intendedRole ?? ''}&profileCode=${partnerProfile ?? ''}&brType=${brType ?? ''}&brMasterNumber=${brContext?.busRelMasterNumber ?? ''}&busRelUid=${brContext?.busRelUid ?? ''}&brSubType=${brContext?.brSubType ?? ''}&partnerStructureSubType=${partner?.partnerStructureSubType ?? ''}&partnerType=${partner?.partnerType ?? ''}&brProfile=${brProfile ?? ''}&operationalType=${partner?.operationalType ?? ''}`;
  const partnerProfileQueryParams = new URLSearchParams(
    profilingWizardParams.startsWith('?') ? profilingWizardParams.slice(1) : profilingWizardParams,
  );
  if (isStringNotBlank(distributeDocument)) {
    partnerProfileQueryParams.set('distributeDocument', distributeDocument);
  }
  if (isStringNotBlank(clientClassificationId)) {
    partnerProfileQueryParams.set('clientClassificationId', clientClassificationId);
  }
  const partnerProfileQueryString = partnerProfileQueryParams.toString();
  const partnerProfileQuerySuffix = isStringNotBlank(partnerProfileQueryString) ? `?${partnerProfileQueryString}` : '';

  const [tabsDisabled, setTabsDisabled] = useState(false);

  useEffect(() => {
    if (isNotNullOrUndefined(partnerProfile)) {
      postSetPartnerProfileMessage(partnerProfile);
    }
  }, [partnerProfile, intendedRole, brType, selectedIntendedRole, selectedBrContext]);

  useEffect(() => {
    if (Object.values(PartnerFormTabItems).includes(tab)) {
      dispatch(setTabId(tab));
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    function handleMessage(event: any) {
      const { message, clearPcdUrl } = event.data;

      if (clearPcdUrl) {
        setPcdV2DistributionUrl(`${basePcdV2Url}/distributions-overview`);
      }

      switch (message) {
        case ClmPortalIncomingPostMessageTypes.REDIRECT_DOC_TAB_V2: {
          const { distributionRequestId, initiator } = event.data.value;
          if (distributionRequestId) {
            setPcdV2DistributionUrl(`/distribution/${distributionRequestId}?initiator=${initiator}`);
          }
          dispatch(setTabId(PartnerFormTabItems.DISTRIBUTION));
          break;
        }
        case ClmPortalIncomingPostMessageTypes.NEW_TAB_OPEN_DOCUMENT: {
          const { documentBlob } = event.data.value;
          const url = URL.createObjectURL(documentBlob);
          const pdfWindow = window.open();
          if (pdfWindow) {
            (pdfWindow as any).location.href = url;
          }

          break;
        }
        // TODO (partner DLM v2): verify this still fires from the iframe once the iframe owns the
        // DLM lifecycle — it carries no status/version, so it is kept for now.
        case ClmPortalIncomingPostMessageTypes.RE_FETCH_PARTNER: {
          setTimeout(function () {
            refetch().finally(() => setTabsDisabled(false));
            fetchBrRelations();
          }, 1000);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.UPDATE_INTENDED_ROLE: {
          setIntendedRole(event.data.value.roleType);

          const receivedBrContext = isProfilingWizardBrChangesEnabled
            ? event.data.value.brType
            : {
                brType: event.data.value.brType,
                brSubType: '',
                busRelMasterNumber: '',
                busRelUid: '',
              };
          setBRContext(receivedBrContext);
          setBrProfile(event.data.value.brProfileType);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.SHOULD_DISABLE_TABS: {
          setTabsDisabled(event.data.value);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_CORE_DATA_TAB: {
          dispatch(setTabId(PartnerFormTabItems.CORE_DATA));

          break;
        }
        case ClmPortalIncomingPostMessageTypes.SAVED_PARTNER: {
          refetch().finally(() => setTabsDisabled(false));

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_PARTNER_PROFILE: {
          dispatch(setTabId(PartnerFormTabItems.PARTNER_PROFILE));
          // When returning from PCD (clearPcdUrl === true), strip the distribution modal params
          // from the browser URL so the partner-profile iframe does not reopen the modal on reload.
          if (clearPcdUrl) {
            const currentParams = new URLSearchParams(location.search);
            currentParams.delete('distributeDocument');
            currentParams.delete('clientClassificationId');
            const newSearch = currentParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash ?? ''}`, {
              replace: true,
            });
          }
          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB: {
          changeTab();

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_INVESTMENT_PROFILE: {
          dispatch(setTabId(PartnerFormTabItems.INVESTMENT_PROFILE));

          break;
        }
        case ClmPortalIncomingPostMessageTypes.OPEN_NEW_BR_TAB: {
          const { brId, brTab, hash } = event.data.value;
          window.open(`/business-relation/edit/${brId}/${brTab}${isNotNullOrUndefined(hash) ? '#' + hash : ''}`);
          break;
        }
        // No default
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [intendedRole, brType, selectedIntendedRole, selectedBrContext, isProfilingWizardBrChangesEnabled]);

  useEffect(() => {
    return () => {
      dispatch(resetDlmState());
      //Reset tab on unmount
      dispatch(setTabId(PartnerFormTabItems.CORE_DATA));
    };
  }, []);

  const handleTabChange = async (_: SyntheticEvent, newValue: React.SetStateAction<any>) => {
    const iframe = getIFrame(shadowRoot);
    if (tabsNeedingSaveValidation.includes(legalPartnerFormTab) && isNotNullOrUndefined(iframe)) {
      postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.CHANGE_TAB_SAVE_CHECK);
      tabRef.current = newValue;
    } else {
      tabRef.current = newValue;
      changeTab();
    }
  };

  const persistReceivedValuesFromIFrame = () => {
    setSelectedBrContext(brContext);
    setSelectedIntendedRole(intendedRole);
    setSelectedProfileCode(partnerProfile);
  };

  const changeTab = (hash?: string) => {
    if (tabRef.current !== tab) {
      persistReceivedValuesFromIFrame();
    }
    dispatch(setTabId(tabRef.current));
    navigate(`/partner/legal-entity/edit/${techId}/${tabRef.current}${hash ?? ''}`, { replace: true });
    setPcdV2DocOverviewUrl(
      isStringNotBlank(urlSearchDocumentId)
        ? `${pcdV2DocOverviewUrl}?documentId=${urlSearchDocumentId}`
        : pcdV2DocOverviewUrl,
    );
  };

  const { onClick: copyOnClick, feedbackMessage: copyToClipboardFeedbackMessage } = useCopyToClipboard();

  return (
    <>
      <PageTitleToken alternateTitle={getPartnerPageTitle(isUatEnvironment, partner?.globalPartnerId)} />
      <TabContext value={legalPartnerFormTab}>
        <PartnerTabsMessageHandler />
        <AppBar position="static" sx={partnerTabStyles.topBar} elevation={0}>
          <Grid container columnSpacing={2}>
            <Grid item xs={7}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  minHeight: '68px',
                }}
              >
                {!isFetching && hasMultipleTabs && (
                  <MultiTabWarning
                    tooltipText={t('multipleTabs.warning', { type: t('multipleTabs.warning.partner') })}
                  />
                )}
                <Typography
                  variant="h1"
                  color={JBColors.white}
                  sx={{
                    backgroundColor: JBColors.core.reflexBlue['50'],
                    paddingX: '8px',
                    borderRadius: '2px',
                  }}
                  data-testid="partner-label-icon"
                >
                  {t('partner-label-icon')}
                </Typography>
                <Tooltip
                  arrow
                  title={
                    isNotNullOrUndefined(copyToClipboardFeedbackMessage) ? copyToClipboardFeedbackMessage : partnerName
                  }
                  componentsProps={{
                    tooltip: {
                      'data-testid': 'partner-title-tooltip',
                    } as any,
                  }}
                >
                  <Typography
                    variant="h1"
                    color="primary"
                    sx={{ ...partnerTabStyles.title, ...partnerTabStyles.cursorPointer }}
                    title={partnerName}
                    onClick={() => copyOnClick(partnerName)}
                    data-testid="partner-name-header"
                  >
                    {isFetching ? <Skeleton variant="text" width="250px" /> : partnerName}
                  </Typography>
                </Tooltip>
                {partner?.globalPartner ? (
                  <SvgIcon sx={partnerTabStyles.titleIcon}>
                    <GlobalLE fill={palette.primary.main} id="global-partner-icon" />
                  </SvgIcon>
                ) : (
                  <SvgIcon sx={partnerTabStyles.titleIcon}>
                    <LegalEntityIcon fill={palette.primary.main} />
                  </SvgIcon>
                )}
                <Typography variant="body1" sx={partnerTabStyles.superscript}>
                  {partner ? formatEnum(partner?.legalPartnerSubType) : '-'}
                </Typography>
                <Typography variant="h5" sx={partnerTabStyles.partnerId}>
                  {partner ? `(${partner?.globalPartnerId})` : ''}
                </Typography>
                <SanctionsPartnerType />
              </Box>
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignContent="center"
                alignItems="center"
                sx={{ height: '100%' }}
                gap={2}
              >
                <Grid item xs={9} md={7} lg={9}>
                  <Lock
                    techId={techId}
                    partnerLocks={partnerLocks}
                    shouldShowDraftSwitch={
                      legalPartnerFormTab === PartnerFormTabItems.CORE_DATA && isNotNullOrUndefined(userId)
                    }
                  />
                </Grid>
                <Grid item>
                  <Button
                    sx={{ maxHeight: '32px' }}
                    id="revision-history-button"
                    variant="outlined"
                    data-testid="revision-history-button"
                    onClick={() =>
                      dispatch(
                        openModal({
                          modalType: isPartnerLockEnabled ? 'RevisionHistoryModal' : 'EditPartnerHistoryModal',
                          modalProps: {
                            partnerType: PartnerType.LEGAL_PARTNER,
                            techId,
                          },
                        }),
                      )
                    }
                  >
                    <JBIcon iconName="changeHistory" size="md" fill={JBColors.core.reflexBlue[100]} />
                  </Button>
                </Grid>
                <Grid item>
                  <RejectWithdrawButton
                    partnerType={PartnerType.LEGAL_PARTNER}
                    isPartnerLockEnabled={!!isPartnerLockEnabled}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AppBar>

        <TwoLevelTabNavigation
          onTabChange={handleTabChange}
          selectedTab={legalPartnerFormTab}
          secondLevelBackgroundColor="#E3E4F2"
          tabItems={[
            {
              value: PartnerFormTabItems.CORE_DATA,
              label: t('partnerTabs.menu.coreData', 'Core data'),
              category: PartnerFormCategories.PROFILE,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.PARTNER_PROFILE,
              label: t('partnerTabs.menu.partnerProfile', 'Partner profile'),
              category: PartnerFormCategories.PROFILE,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.WEALTH_OVERVIEW,
              label: t('partnerTabs.menu.wealthProfile', 'Wealth profile'),
              category: PartnerFormCategories.PROFILE,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.RISK_CONSIDERATIONS,
              label: t('partnerTabs.menu.riskConsiderations', 'Risk considerations'),
              category: PartnerFormCategories.PROFILE,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.INVESTMENT_PROFILE,
              label: t('partnerTabs.menu.investmentProfile', 'Investment profile'),
              hidden: isUserApac,
              category: PartnerFormCategories.PROFILE,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.ACCOUNT_RELATIONS,
              label: t('partnerTabs.menu.p2br', 'Business relations'),
              hidden: isUserApac,
              category: PartnerFormCategories.RELATIONSHIPS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.RELATIONS,
              label: t('partnerTabs.menu.p2p', 'Partner relations'),
              hidden: isUserApac,
              category: PartnerFormCategories.RELATIONSHIPS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.CONTACT_NOTES,
              label: t('partnerTabs.menu.contactNotes', 'Contact notes'),
              hidden: !isContactNoteTabEnabled,
              category: PartnerFormCategories.INTERACTIONS,
              iconPosition: 'end',
              sx: { marginRight: '30px' },
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.ACTIVITY_TIMELINE,
              label: t('partnerTabs.menu.activityTimeline', 'Activity timeline'),
              hidden: isUserApac,
              category: PartnerFormCategories.INTERACTIONS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.DISTRIBUTION,
              label: t('partnerTabs.menu.distribution', 'Distribution'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.DOCUMENT_OVERVIEW,
              label: t('partnerTabs.menu.documentOverview', 'Document overview'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.DOC_COLLECTION,
              label: t('partnerTabs.menu.docCollection', 'Doc collection'),
              hidden: isUserApac,
              category: PartnerFormCategories.DOCUMENTS,
              disabled: tabsDisabled,
            },
            {
              value: PartnerFormTabItems.PRODUCTS_AND_SERVICES,
              label: t('tabs.menu.categories.products-and-services', 'Products & services'),
              hidden: isUserApac,
              category: PartnerFormCategories.PRODUCTS_AND_SERVICES,
              disabled: tabsDisabled,
            },
          ]}
        />

        {legalPartnerFormTab === PartnerFormTabItems.CORE_DATA && (
          <PartnerFrontendIFrame
            path={`/partner/legal-entity/edit/${techId}/core-data?brType=${selectedBrContext?.brType ?? ''}&brMasterNumber=${selectedBrContext?.busRelMasterNumber ?? ''}&busRelUid=${selectedBrContext?.busRelUid ?? ''}&intendedRole=${selectedIntendedRole}&profileCode=${
              selectedProfileCode ?? ''
            }${dlmUrlParams}&wizardBrUid=${wizardBrUid ?? ''}&wizardRole=${wizardRole ?? ''}`}
            loading={
              isPartnerDlmV2 === undefined ||
              (isPartnerLockEnabled &&
                !isPartnerDlmV2 &&
                isNullOrUndefined(initialDlmStatusAndVersion) &&
                isNotNullOrUndefined(user))
            }
            heightOffset={showSecondLevelTabs ? '218px' : '174px'}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.PARTNER_PROFILE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.partnerProfileIframe}/partner-profile/partner/${techId}${partnerProfileQuerySuffix}${isNotNullOrUndefined(urlHash) ? urlHash : ''}`}
            title={'Partner profile'.toUpperCase()}
            id="pp-iframe"
            height={frameHeight}
            loading={isPartnerProfileLoading}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.WEALTH_OVERVIEW && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.wealthOverviewIframe}/wealth-overview/partner/${partner.techId}${profilingWizardParams}`}
            title={'Wealth Overview'.toUpperCase()}
            id="wo-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.INVESTMENT_PROFILE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.investmentProfileIframe}/investment-profile/partner?partnerId=${partner.techId}`}
            title={'Investment profile'.toUpperCase()}
            id="investment-profile-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.CONTACT_NOTES && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.contactNoteIframe}/contact-notes/${techId}${isStringNotBlank(newNote) ? '/create' : ''}`}
            title="Contact notes"
            id="contact-notes-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.RELATIONS && (
          <PartnerFrontendIFrame
            path={`/partner/legal-entity/edit/${techId}/relations${profilingWizardParams}`}
            loading={isPartnerProfileLoading}
            heightOffset={showSecondLevelTabs ? '218px' : '174px'}
          />
        )}
        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.DISTRIBUTION && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DistributionUrl}`}
            title={'Documents'.toUpperCase()}
            id="documents-iframe"
            height={frameHeight}
          />
        )}
        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.DOCUMENT_OVERVIEW && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DocOverviewUrl}`}
            title={'Documents'.toUpperCase()}
            id="documents-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.DOC_COLLECTION && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.dossierIframe}/documents/partner/${techId}`}
            title={'Documents'.toUpperCase()}
            id="doc-collection-iframe"
            height={frameHeight}
          />
        )}
        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.ACCOUNT_RELATIONS && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.brCoreIframe}/partner/edit/${partner.techId}/business-relationship`}
            title={'Account Relation'.toUpperCase()}
            id="pcm-br-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.PRODUCTS_AND_SERVICES && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.productAndServicesIframe}${pcoUrl}`}
            title={'Products & Services'.toUpperCase()}
            id="pco-iframe"
            height={frameHeight}
          />
        )}

        {partner?.techId && legalPartnerFormTab === PartnerFormTabItems.ACTIVITY_TIMELINE && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.catActivityTimelineIframe}/partner/activities?partnerNumber=${partner.techId}`}
            title={'Activity Timeline'.toUpperCase()}
            id="cat-iframe"
            height={frameHeight}
          />
        )}

        {partner && legalPartnerFormTab === PartnerFormTabItems.RISK_CONSIDERATIONS && (
          <EmbeddedIFrame
            url={`${runtimeConfig.configData.iframeUrls.riskConsiderationsIframe}/partner/${partner.techId}${profilingWizardParams}`}
            title={'Risk Considerations'.toUpperCase()}
            id="risk-considerations-iframe"
            height={frameHeight}
          />
        )}
      </TabContext>
    </>
  );
};

export default LegalPartnerFormTabs;
